import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Skeleton, Stack, Typography } from '@mui/material'

type StatusValue<T = ReactNode> = { value?: T; isLoading?: boolean; failed?: boolean }

type AnalysisStatusLinesProps = {
  marketIso: string
  latestAnalyzedVersion: StatusValue
  hasFtue: StatusValue<boolean>
  reviewPublished: boolean
  screenshotCount: StatusValue
}

export const AnalysisStatusLines: FC<AnalysisStatusLinesProps> = ({ marketIso, latestAnalyzedVersion, hasFtue, reviewPublished, screenshotCount }) => {
  const { t } = useTranslation()
  const yesNo = (value: boolean) => t(value ? 'internal-common:yes' : 'internal-common:no')

  const renderValue = ({ value, isLoading, failed }: StatusValue) => {
    if (isLoading) return <Skeleton width={60} />
    if (failed) return <Typography color="error.main">{t('internal-analysts:analysis_overview_status_failed')}</Typography>
    return <Typography>{value}</Typography>
  }

  const lines: { label: string; value: StatusValue }[] = [
    { label: t('internal-analysts:analysis_overview_latest_manual_version', { market: marketIso.toUpperCase() }), value: latestAnalyzedVersion },
    { label: t('internal-analysts:analysis_overview_ftue'), value: { ...hasFtue, value: yesNo(!!hasFtue.value) } },
    { label: t('internal-analysts:analysis_overview_review'), value: { value: yesNo(reviewPublished) } },
    { label: t('internal-analysts:analysis_overview_screenshot_count'), value: screenshotCount },
  ]

  return (
    <Stack spacing={0.5}>
      {lines.map(({ label, value }) => (
        <Stack key={label} direction="row" spacing={1} alignItems="center">
          <Typography fontWeight="bold">{label}:</Typography>
          {renderValue(value)}
        </Stack>
      ))}
    </Stack>
  )
}
